// Moving the user's map for Artemis's set_map_view client action (WEB-7111, API-3681), on the
// Google map or the MapLibre (3D) one. A view is { bbox: [w, s, e, n] } or { center: [lng, lat],
// zoom }, with an optional bearing (degrees clockwise from north).

const MAX_ZOOM = 20
const PADDING_PX = 60
const DURATION_MS = 800

const finite = v => typeof v === 'number' && Number.isFinite(v)
const validLng = v => finite(v) && v >= -180 && v <= 180
const validLat = v => finite(v) && v >= -85 && v <= 85

// The action's Input as a view to move to, or null when it can't be one. The server has already
// checked it (API-3681); this is the app refusing to move the map somewhere nonsensical anyway.
export const viewFromInput = input => {
  if (!input || typeof input !== 'object') return null
  const bearing = finite(input.bearing) ? ((input.bearing % 360) + 360) % 360 : null
  const bbox = input.bbox
  if (Array.isArray(bbox) && bbox.length === 4) {
    const [w, s, e, n] = bbox
    if (validLng(w) && validLng(e) && validLat(s) && validLat(n) && w < e && s < n) return { bbox: [w, s, e, n], bearing }
  }
  const center = input.center
  if (Array.isArray(center) && center.length === 2 && validLng(center[0]) && validLat(center[1])) {
    const zoom = finite(input.zoom) ? Math.max(0, Math.min(MAX_ZOOM, input.zoom)) : null
    return { center: [center[0], center[1]], zoom, bearing }
  }
  return null
}

const isGoogleMap = map => {
  const googleMaps = typeof window !== 'undefined' && window.google && window.google.maps
  return !!(googleMaps && googleMaps.Map && map instanceof googleMaps.Map)
}

const valueOf = v => (typeof v === 'function' ? v() : v)

// Where the map is now, as a view, so a move can be undone. Null when the map can't say.
export const readView = map => {
  try {
    const c = map && map.getCenter && map.getCenter()
    if (!c) return null
    const lng = valueOf(c.lng), lat = valueOf(c.lat)
    if (!finite(lng) || !finite(lat)) return null
    const zoom = map.getZoom ? map.getZoom() : null
    const bearing = map.getHeading ? map.getHeading() : map.getBearing ? map.getBearing() : null
    return { center: [lng, lat], zoom: finite(zoom) ? zoom : null, bearing: finite(bearing) ? bearing : null }
  } catch (e) {
    return null
  }
}

// Moves the map to a view. Returns false when this map can't be moved that way.
export const moveMap = (map, view) => {
  if (!map || !view) return false
  if (isGoogleMap(map)) {
    const googleMaps = window.google.maps
    if (view.bbox) {
      const [w, s, e, n] = view.bbox
      map.fitBounds(new googleMaps.LatLngBounds({ lat: s, lng: w }, { lat: n, lng: e }), PADDING_PX)
    } else {
      map.panTo({ lat: view.center[1], lng: view.center[0] })
      if (finite(view.zoom)) map.setZoom(view.zoom)
    }
    // Only vector Google maps turn; on raster ones setHeading does nothing.
    if (finite(view.bearing) && map.setHeading) map.setHeading(view.bearing)
    return true
  }
  // MapLibre.
  const rotate = finite(view.bearing) ? { bearing: view.bearing } : {}
  if (view.bbox && map.fitBounds) {
    const [w, s, e, n] = view.bbox
    map.fitBounds([[w, s], [e, n]], { padding: PADDING_PX, duration: DURATION_MS, ...rotate })
    return true
  }
  if (view.center && map.flyTo) {
    map.flyTo({ center: view.center, ...(finite(view.zoom) ? { zoom: view.zoom } : {}), duration: DURATION_MS, ...rotate })
    return true
  }
  return false
}
